import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation, Trans } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useDialogControllers } from '@machobear/design-system'
import useCurrentSession from '@/hooks/use-current-session'
import { breakpoints, fontWeights, colors } from '@/styles'
import prices from '@/lib/prices'
import { unlock, view } from '@/models/plans/apis'
import useRole from '@/hooks/use-role'
import { useAvailableCreditChecks } from '@/hooks/use-block-and-pay'
import useFinancialFiles from '@/hooks/use-financial-files'
import checkCredits from '@/utils/check-credits'
import { showSampleModal } from '@/features/Application/sample-modal'
import { PageTitle, FloatingModal } from '@/features/Application/elements'
import { View as InsuranceView } from '@/features/Application/insurance'
import { View as AutomobilesView } from '@/features/Application/automobiles'
import { ApplicationDocumentsView } from '@/features/Application/financial-details'
import { TrustScoreUnlock, TrustScoreUnlockGrey, UnlockedLock } from '@/components/icons'
import { useGetProduct } from '@/hooks/use-block-and-pay'
import useGetCreditReports from '@/hooks/use-get-credit-report'
import { Button } from '@/components/button'
import { PaymentProvider } from '@/components/ds-checkout-modal/payment-context'
import { TrustScorePaymentModal } from '@/features/Application/trust-score/view/trust-score-payment-modal'
import NoBillingUserDialog from '@/features/SponsoredListing/modals/no-billing-user-modal'
import { pushToDatalayer } from '@/utils/push-datalayer'

const tabletViewBreakpoint = '1400px'

const Wrapper = styled.div`
  position: relative;
  max-width: 900px;
  background: ${colors.grey96};
  border: 1px solid ${colors.grey88};
  border-radius: 0px 8px 8px 8px;
  padding: 45px 20px 20px;
  margin-top: 59px;

  @media screen and (max-width: ${breakpoints.desktopSmall}) {
    border-radius: 8px;
    margin-top: 10px;
    margin-bottom: 40px;
  }
`

const RelativeContainer = styled.div`
  position: relative;
  height: ${props => (props.isLockedState ? '350px' : 'auto')};
`

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: ${colors.grey16};
  margin: 0;
  padding: ${({ padding }) => (padding ? padding : '0px')};
  width: 100%;
  margin-bottom: ${({ marginBottom }) => (marginBottom ? marginBottom : '0px')};
  text-align: center;

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    font-size: 14px;
  }
`

const ButtonContainer = styled.div`
  display: grid;
  margin-top: 20px;
  width: 100%;
  justify-content: center;
  grid-template-columns: max-content;

  @media screen and (max-width: ${tabletViewBreakpoint}) {
    grid-template-columns: 1fr;
  }

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: max-content;
  }

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    grid-template-columns: 1fr;
  }
`

const Link = styled.a`
  text-decoration: none;
  color: ${colors.link};
  cursor: pointer;
`

const Bold = styled.span`
  font-weight: ${fontWeights.bold};
`

const DocumentsView = ({ application = {}, hasUnlockCredits, applicationLoading }) => {
  const { t } = useTranslation()
  const userRole = useRole()
  const dispatch = useDispatch()
  const session = useCurrentSession()
  const submittedFilesCount = useFinancialFiles(
    application?.user_info?.files?.user_secure_files,
    userRole
  )?.length
  const { state, status } = application?.trust_score ?? {}
  const isLocked = state === 'locked' || state === 'analyzing'
  const showViewScore = isLocked && status !== 'active'
  const isAnalyzing = state === 'analyzing'
  const applicantFullName = `${application?.user_info?.first_name} ${application?.user_info?.last_name}`
  const [submitting, setSubmitting] = useState(false)
  const client = useQueryClient()
  const { availableCreditChecks, creditDetails } = useAvailableCreditChecks()
  const { data: products } = useGetProduct('credit-check-single-package')
  const creditsRemaining = availableCreditChecks >= 0 ? parseInt(availableCreditChecks) : 0
  const no_credit_history_or_not_enough_info =
    status === 'no_credit_history' || status === 'not_enough_info'

  const applicationDetails = {
    name: applicantFullName,
    id: application?.id
  }
  const { error } = useGetCreditReports(application?.id)
  const { visible, showModal, closeModal } = useDialogControllers({
    trustScore: false,
    editPaymentDialog: false,
    trustScoreAgreementDialog: false,
    trustScoreAgreementNoDataDialog: false
  })
  const trustScoreDialogId =
    error?.message_key === 'credit_report_submitted_data_not_found'
      ? 'trustScoreAgreementNoDataDialog'
      : 'trustScoreAgreementDialog'
  const getNewTrustScore = async () => {
    client.invalidateQueries({ queryKey: ['block'] })
    await dispatch.application.getApplicationTrustScore(application?.id)
  }

  const unlockTrustScore = async () => {
    setSubmitting(true)
    try {
      await unlock(applicationDetails.id, {
        unlocked: 1,
        viewed: 1,
        source: checkCredits(creditDetails)
      })
      pushToDatalayer({
        event: 'application-trust-score-documents-submit-purchase-credit'
      })
      getNewTrustScore()
      client.invalidateQueries({ queryKey: ['blocks'] })
    } catch (err) {
      setSubmitting(false)
      throw err
    }
    setSubmitting(false)
  }

  const viewScore = async () => {
    setSubmitting(true)
    try {
      await view(applicationDetails.id, {
        source: checkCredits(creditDetails)
      })
      getNewTrustScore()
    } catch (err) {
      throw err
    }
    setSubmitting(false)
  }

  const openModal = () => {
    const isBillingUserValid = session?.billing_user_valid
    showModal(isBillingUserValid ? trustScoreDialogId : 'noBillingUserDialog')()
  }

  const getUnlockButton = () => {
    if (showViewScore) {
      if (status === 'no_credit_history') {
        return (
          <Button
            id="application-trust-score-documents-locked-view"
            onClick={viewScore}
            theme="primary"
            disabled={submitting}
          >
            {t('l.application.view.trust_score.button', 'View Trust Score')}
          </Button>
        )
      } else if (status === 'not_enough_info') {
        return (
          <Button
            id="application-trust-score-documents-locked-view"
            onClick={openModal}
            theme="primary"
            disabled={submitting}
          >
            {t('l.application.view.trust_score.button', 'View Trust Score')}
          </Button>
        )
      } else {
        return (
          <Button
            id={
              hasUnlockCredits
                ? 'application-trust-score-documents-submit-purchase-credit'
                : 'application-trust-score-documents-locked-unlock'
            }
            theme="primary"
            onClick={openModal}
            disabled={submitting}
          >
            <UnlockedLock />
            {t('l.application.view.unlock.button', 'Unlock Trust Score')}
          </Button>
        )
      }
    } else if (isAnalyzing) {
      return (
        <Button
          id="application-trust-score-documents-analyze-view-exp"
          onClick={showSampleModal}
          theme="secondary"
        >
          {t('l.application.view.view_example.button', 'View Example')}
        </Button>
      )
    } else {
      return (
        <Button
          id={
            hasUnlockCredits
              ? 'application-trust-score-documents-submit-purchase-credit'
              : 'application-trust-score-documents-locked-unlock'
          }
          theme="primary"
          onClick={openModal}
          disabled={submitting}
        >
          <UnlockedLock />
          {t('l.application.view.unlock.button', 'Unlock Trust Score')}
        </Button>
      )
    }
  }
  return (
    <Wrapper id="documents">
      <PageTitle
        noMobileMarginTop
        title={t(
          'b.application.view.tenancy_application.verified_documents.title',
          'Verified Documents'
        )}
      />
      <RelativeContainer isLockedState={isLocked || isAnalyzing}>
        {(isLocked || isAnalyzing) && (
          <FloatingModal
            logo={!isAnalyzing ? <TrustScoreUnlock /> : <TrustScoreUnlockGrey />}
            title={
              !isAnalyzing
                ? t('l.application.view.documents_locked.title', 'Documents Locked')
                : t('l.application.view.analyzing_trust_score.title', 'Analyzing Trust Score')
            }
          >
            {!isAnalyzing ? (
              <Paragraph marginBottom="14px">
                {no_credit_history_or_not_enough_info ? (
                  <Trans
                    submittedFilesCount={submittedFilesCount}
                    i18nKey="l.application.view.documents_locked.no_credit_history_or_not_enough_info.text"
                  >
                    This applicant has submitted <Bold>{{ submittedFilesCount }}</Bold> verified
                    documents. The Trust Score helps us support renters of all backgrounds, by
                    looking beyond their Canadian credit score.
                  </Trans>
                ) : creditsRemaining > 0 ? (
                  <Trans
                    submittedFilesCount={submittedFilesCount}
                    creditsRemaining={creditsRemaining}
                    i18nKey="l.application.view.documents_locked.credits_remaining.text"
                  >
                    This applicant has submitted <Bold>{{ submittedFilesCount }}</Bold> documents
                    that have been manually verified by the liv.rent team. You can unlock these
                    documents and this renter's full Trust Score report by using 1 of your{' '}
                    <Bold>{{ creditsRemaining }}</Bold> remaining credits.
                  </Trans>
                ) : (
                  <Trans
                    submittedFilesCount={submittedFilesCount}
                    i18nKey="l.application.view.documents_locked.no_credits_remaining.text"
                  >
                    This applicant has submitted <Bold>{{ submittedFilesCount }}</Bold> documents
                    that have been manually verified by the liv.rent team. You can unlock these
                    documents and this renter's full Trust Score by purchasing a report for $
                    {{ price: prices.trustScore.unlock }}.
                  </Trans>
                )}
              </Paragraph>
            ) : (
              <>
                <Paragraph marginBottom="14px">
                  {t(
                    'l.application.view.documents_anayzling_text_1.text',
                    "We are still in the process of verifying this applicant's documentation and calculating their Trust Score. Please check back shortly."
                  )}
                </Paragraph>
                <Paragraph marginBottom="14px">
                  <Trans i18nKey="l.application.view.analyzing_text_2.text">
                    {' '}
                    If you continue to see this message after 1-3 business days, please get in touch
                    with us at
                    <br />
                    <Link href="mailto:support@liv.rent" target="_blank" rel="noopener noreferrer">
                      support@liv.rent
                    </Link>
                  </Trans>
                </Paragraph>
              </>
            )}

            <ButtonContainer>{getUnlockButton()}</ButtonContainer>
          </FloatingModal>
        )}

        <ApplicationDocumentsView
          application={application}
          isLocked={isLocked}
          applicationLoading={applicationLoading}
        />
      </RelativeContainer>
      <InsuranceView application={application} applicationLoading={applicationLoading} />
      <AutomobilesView application={application} applicationLoading={applicationLoading} />
      <PaymentProvider>
        <TrustScorePaymentModal
          products={products}
          visible={visible}
          showModal={showModal}
          closeModal={closeModal}
          getNewTrustScore={getNewTrustScore}
          applicationId={application?.id}
          hasUnlockCredits={hasUnlockCredits}
          unlockTrustScore={unlockTrustScore}
        />
      </PaymentProvider>
      <NoBillingUserDialog
        open={visible.noBillingUserDialog}
        onClose={closeModal('noBillingUserDialog')}
      />
    </Wrapper>
  )
}

export default DocumentsView
